import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BrandLogo from '@/components/BrandLogo';
import { toast } from '../utils/toast';
import { Loader2, Monitor, LogIn, Shield, Activity, Heart, Menu, X } from 'lucide-react';
import Footer from '@/components/Footer';
import axios from 'axios';
import { getAuthenticatedLandingPath, getSessionReturnPath, rememberAuthenticatedPath, restoreStoredSession } from '../utils/authSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignInPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [checkingSession, setCheckingSession] = useState(() => Boolean(localStorage.getItem('token')));
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const restoredUser = await restoreStoredSession();
      if (cancelled) return;
      if (restoredUser) {
        navigate(getSessionReturnPath(restoredUser), { replace: true });
        return;
      }
      setCheckingSession(false);
    };

    if (localStorage.getItem('token')) {
      restore();
    } else {
      setCheckingSession(false);
    }

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.username) {
      toast.error('Username is required.');
      return;
    }
    if (!formData.password) {
      toast.error('Password is required.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signin`, {
        ...formData,
        username: formData.username.trim()
      });
      
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.user.session_id) {
        localStorage.setItem('session_id', response.data.user.session_id);
      }
      
      setTimeout(() => {
        // First login with a temporary password → force a password change first
        if (response.data.user.is_first_login) {
          navigate('/first-login');
          return;
        }
        const landingPath = getAuthenticatedLandingPath(response.data.user);
        rememberAuthenticatedPath(landingPath);
        navigate(landingPath);
      }, 1000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred during sign-in.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="signin-page min-h-screen flex flex-col">
      {/* Header */}
      <header className="signin-header bg-white border-b border-gray-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 signin-header-inner">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <BrandLogo />
            </div>

            {/* Desktop Navigation & Buttons - All aligned right with equal spacing */}
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/solutions')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                The solution
              </button>
              <button onClick={() => navigate('/pricing')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium transition-colors">
                Contact us
              </button>
              <Button 
                onClick={() => navigate('/request-demo')}
                className="bg-[#10B981] hover:bg-[#059669] text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <Monitor className="w-4 h-4 mr-2" />
                Request for demo
              </Button>
              <Button 
                onClick={() => navigate('/signin')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm px-4 py-2 rounded-lg transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3 border-t border-gray-200 pt-4">
              <button 
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => { navigate('/solutions'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                The solution
              </button>
              <button 
                onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Contact us
              </button>
              <div className="space-y-2 pt-2">
                <Button 
                  onClick={() => { navigate('/request-demo'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white text-sm"
                >
                  Request for demo
                </Button>
                <Button 
                  onClick={() => { navigate('/signin'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 text-white text-sm"
                >
                  Sign in
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <main className="signin-main flex-1 grid md:grid-cols-2">
        {/* Left Side - Image with Security/Healthcare Icons */}
        <div className="hidden md:block relative bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900">
          <img
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1000&h=1000&fit=crop"
            alt="Healthcare Professional Login"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 to-cyan-900/60"></div>
          
          {/* Overlay Icons */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid grid-cols-2 gap-8">
              {/* Lock/Security Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <Shield className="w-16 h-16 text-white" />
              </div>
              
              {/* Healthcare Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <Activity className="w-16 h-16 text-white" />
              </div>
              
              {/* Heart Monitor Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <Heart className="w-16 h-16 text-white" />
              </div>
              
              {/* Login Icon */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                <LogIn className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Sign In Form */}
        <div className="signin-form-panel flex items-center justify-center p-6 md:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="signin-mobile-logo hidden">
              <BrandLogo />
            </div>
            <div className="signin-mobile-intro hidden">
              <h1>Sign in</h1>
              <p>Access your training dashboard and continue learning.</p>
            </div>
            {/* Sign Up Prompt */}
            <div className="signin-signup-prompt text-right mb-6">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/signup')}
                  data-testid="signup-link"
                  className="text-[#0056B3] hover:text-[#00AEEF] font-semibold underline"
                >
                  Sign up now
                </button>
              </p>
            </div>

            {/* Sign In Card */}
            <div className="signin-card bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <div className="mb-6">
                <h1 className="signin-desktop-title text-3xl font-bold text-gray-900 mb-2">Sign in</h1>
                <p className="signin-desktop-subtitle text-sm text-gray-600">Access your secure training dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="signin-form">
                {/* Username Field */}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                    Username
                  </Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    data-testid="signin-username-input"
                    placeholder="Enter your username here"
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full h-11 rounded-lg border-gray-300 focus:border-[#0056B3] focus:ring-[#0056B3] bg-white"
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="signin-password-input"
                      placeholder="Enter your password here"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full h-11 rounded-lg border-gray-300 focus:border-[#0056B3] focus:ring-[#0056B3] bg-white pr-16"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0056B3] hover:text-[#00AEEF] text-xs font-semibold uppercase"
                    >
                      {showPassword ? 'HIDE' : 'SHOW'}
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    data-testid="forgot-password-link"
                    className="text-sm text-[#0056B3] hover:text-[#00AEEF] font-medium underline"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  data-testid="signin-submit-btn"
                  disabled={loading}
                  className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90 text-white rounded-lg py-3 text-base font-semibold h-12"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing In...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign in
                    </>
                  )}
                </Button>
              </form>
            </div>
            <div className="signin-legal-links mt-5 flex items-center justify-center gap-4 text-xs text-gray-500">
              <button type="button" onClick={() => navigate('/terms-and-conditions')} className="text-[#0056B3] font-medium underline">
                Terms & Conditions
              </button>
              <span className="text-gray-300">|</span>
              <button type="button" onClick={() => navigate('/privacy-policy')} className="text-[#0056B3] font-medium underline">
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <div className="signin-footer">
        <Footer />
      </div>
    </div>
  );
};

export default SignInPage;
